/**
 * File CDN utility — generates signed URLs for S3 assets.
 *
 * Migrated from scio-v2 FileCdnService + ClientUtilsService.getThumbnail.
 * Uses HMAC-SHA1 signatures (via native Web Crypto) with credentials from
 * `fileCdnkeyData` (ME query). Falls back to direct S3 URLs when CDN is
 * not configured.
 */
import { useState, useEffect } from 'react';
import { useAuthStore } from '@/stores/auth-store';

// --- Types ---

interface CdnKeyData {
  accessKeyId: string;
  secretAccessKey: string;
}

interface SignatureParams {
  Bucket: string;
  Key: string;
  AccessKeyId: string;
  Expires: string;
}

interface CachedUrl {
  url: string;
  expires: number;
}

// --- Config (from env vars) ---

const FILE_CDN_URL = import.meta.env.VITE_FILE_CDN_URL || '';
const FILE_CDN_EXPIRED = parseInt(import.meta.env.VITE_FILE_CDN_EXPIRED || '3600000', 10);
const AWS_BUCKET_NAME = import.meta.env.VITE_AWS_BUCKET_NAME || 'optisigns-assets';

/** 2-minute buffer before considering a cached URL expired (matches scio-v2). */
const EXPIRY_BUFFER_MS = 120_000;

// --- Module-level state ---

let _keyData: CdnKeyData | null = null;
let _cryptoKey: CryptoKey | null = null;
const _urlCache = new Map<string, CachedUrl>();

// --- Internal helpers ---

/** Double-base64 decode fileCdnkeyData to extract AWS credentials. */
function decodeKeyData(fileCdnkeyData: string): CdnKeyData {
  const firstDecode = atob(fileCdnkeyData);
  const secondDecode = atob(firstDecode);
  return JSON.parse(secondDecode);
}

/** Lazily read and decode key data from the auth store. */
function getKeyData(): CdnKeyData | null {
  if (_keyData) return _keyData;
  const me = useAuthStore.getState().me;
  if (!me?.fileCdnkeyData) return null;
  _keyData = decodeKeyData(me.fileCdnkeyData);
  return _keyData;
}

/**
 * URL-encode a string, preserving `/` characters.
 * Matches scio-v2 `ClientUtilsService.fixedEncodeURIComponent`.
 */
function fixedEncodeURIComponent(str: string): string {
  return encodeURIComponent(str)
    .replace(/[!'()*]/g, (c) => '%' + c.charCodeAt(0).toString(16))
    .replace(/%2F/g, '/');
}

/**
 * Normalize an S3 key by decoding any pre-encoded percent sequences.
 * Keys from MongoDB may be stored with `%20` for spaces — we need the raw
 * key (with actual spaces) so the signature matches what the CDN proxy expects.
 */
function normalizeS3Key(key: string): string {
  try {
    return decodeURIComponent(key);
  } catch {
    return key;
  }
}

/** Build the direct S3 fallback URL. */
function getDirectS3Url(bucket: string, key: string): string {
  return `https://${bucket}.s3.amazonaws.com/${fixedEncodeURIComponent(key)}`;
}

/** Import the HMAC-SHA1 CryptoKey from the secret. Cached after first call. */
async function getCryptoKey(secretKey: string): Promise<CryptoKey> {
  if (_cryptoKey) return _cryptoKey;
  const encoder = new TextEncoder();
  _cryptoKey = await crypto.subtle.importKey(
    'raw',
    encoder.encode(secretKey),
    { name: 'HMAC', hash: 'SHA-1' },
    false,
    ['sign'],
  );
  return _cryptoKey;
}

/**
 * Calculate HMAC-SHA1 signature from sorted key-value params using Web Crypto.
 * Matches scio-v2 `ClientUtilsService.calculateSignature`.
 */
async function calculateSignature(obj: SignatureParams, secretKey: string): Promise<string> {
  const sorted = Object.keys(obj)
    .sort()
    .reduce<Record<string, string>>((result, key) => {
      result[key] = obj[key as keyof SignatureParams];
      return result;
    }, {});

  const key = await getCryptoKey(secretKey);
  const encoder = new TextEncoder();
  const data = encoder.encode(JSON.stringify(sorted));
  const sig = await crypto.subtle.sign('HMAC', key, data);
  return Array.from(new Uint8Array(sig))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/** Resolve cache key from s3Key + bucket. */
function resolveCacheParams(s3Key: string, bucket?: string | null) {
  const normalizedKey = normalizeS3Key(s3Key);
  const resolvedBucket = bucket || AWS_BUCKET_NAME;
  const cacheKey = `${resolvedBucket}/${normalizedKey}`;
  return { normalizedKey, resolvedBucket, cacheKey };
}

/** Synchronous cache lookup. Returns cached URL or null. */
function getCachedUrl(s3Key: string, bucket?: string | null): string | null {
  const { cacheKey } = resolveCacheParams(s3Key, bucket);
  const cached = _urlCache.get(cacheKey);
  if (cached && Date.now() + EXPIRY_BUFFER_MS < cached.expires) {
    return cached.url;
  }
  return null;
}

// --- Public API ---

/**
 * Generate a signed CDN URL for an S3 asset key.
 *
 * - Returns cached URL if still valid (with 2-min buffer).
 * - Falls back to direct S3 URL if CDN is not configured or key data is unavailable.
 *
 * @param s3Key — The S3 object key (e.g. `"thumbnails/abc123.jpg"`)
 * @param bucket — Optional bucket override (defaults to `VITE_AWS_BUCKET_NAME`)
 */
export async function getSignedUrl(s3Key: string, bucket?: string | null): Promise<string> {
  const { normalizedKey, resolvedBucket, cacheKey } = resolveCacheParams(s3Key, bucket);

  // Check cache — return if not expiring within 2 minutes
  const cached = _urlCache.get(cacheKey);
  if (cached && Date.now() + EXPIRY_BUFFER_MS < cached.expires) {
    return cached.url;
  }

  // No CDN URL configured → fall back to direct S3
  if (!FILE_CDN_URL) {
    return getDirectS3Url(resolvedBucket, normalizedKey);
  }

  const keyData = getKeyData();
  if (!keyData) {
    return getDirectS3Url(resolvedBucket, normalizedKey);
  }

  const params: SignatureParams = {
    Bucket: resolvedBucket,
    Key: normalizedKey,
    AccessKeyId: keyData.accessKeyId,
    Expires: String(Date.now() + FILE_CDN_EXPIRED),
  };

  const signature = await calculateSignature(params, keyData.secretAccessKey);
  // `origin=true` makes the CDN service redirect to the DigitalOcean Spaces origin instead of the
  // DO CDN edge, which has rejected its presigned URLs with 403 since 2026-09-18 (#2179). The flag
  // is outside the signed params, and DO's CDN never cached presigned requests, so nothing is lost.
  // Covers what the `<img>`/`<video>` S3 fallback below cannot: open-in-new-tab, download, fetch().
  const url = `${FILE_CDN_URL}/${params.Bucket}/${fixedEncodeURIComponent(params.Key)}?AccessKeyId=${params.AccessKeyId}&Expires=${params.Expires}&Signature=${signature}&origin=true`;

  _urlCache.set(cacheKey, { url, expires: +params.Expires });

  return url;
}

/**
 * React hook for signed CDN URLs. Returns the signed URL (or direct S3 fallback).
 * Uses sync cache for instant render when available, then async computation.
 *
 * Subscribes to `fileCdnkeyData` so the URL is recomputed when auth loads.
 * This handles the race where ASSETS_QUERY resolves before ME_QUERY: without
 * this, the first call to getKeyData() returns null (me not yet populated),
 * the hook falls back to a direct S3 URL that returns 403, imgError fires, and
 * the thumbnail stays blank even after auth finishes loading.
 */
export function useSignedUrl(s3Key?: string | null, bucket?: string | null): string | null {
  // Subscribe so the effect re-runs when fileCdnkeyData becomes available
  const fileCdnkeyData = useAuthStore((s) => s.me?.fileCdnkeyData);

  const [url, setUrl] = useState<string | null>(() => {
    if (!s3Key) return null;
    if (s3Key.startsWith('http')) return s3Key;
    return getCachedUrl(s3Key, bucket);
  });

  useEffect(() => {
    if (!s3Key) {
      setUrl(null);
      return;
    }
    if (s3Key.startsWith('http')) {
      setUrl(s3Key);
      return;
    }
    let cancelled = false;
    getSignedUrl(s3Key, bucket).then((signedUrl) => {
      if (!cancelled) setUrl(signedUrl);
    });
    return () => {
      cancelled = true;
    };
  }, [s3Key, bucket, fileCdnkeyData]);

  return url;
}

/**
 * Initialize CDN key from external source (e.g., share link context).
 * Used by share pages where ME_QUERY doesn't run — CDN key comes from
 * getAppSettingsForShareLink instead of authStore.me.fileCdnkeyData.
 */
export function initCdnKeyFromData(fileCdnkeyData: string): void {
  if (!fileCdnkeyData || _keyData) return;
  _keyData = decodeKeyData(fileCdnkeyData);
  _cryptoKey = null; // reset so it gets recreated with new key
}

let _warnedS3Fallback = false;

/**
 * Retry a failed file-CDN `<img>` / `<video>` once via the S3 copy (`&s3=true`).
 *
 * The CDN service 302s to a DigitalOcean edge URL. When that target fails (2026-09-18: the DO
 * edge started rejecting the presigned URLs with 403) the media stays blank. scio1 has always
 * retried with `&s3=true` in its thumbnail `onerror`; this is the same fallback, installed once
 * for every element instead of per component.
 *
 * `error` events don't bubble, so this listens in the capture phase. The first failure is
 * swallowed (`stopPropagation`) so a component's own `onError` placeholder doesn't unmount the
 * element mid-retry. If the S3 retry also fails, the event reaches the component as usual.
 */
export function installFileCdnS3Fallback(): void {
  if (!FILE_CDN_URL) return;
  document.addEventListener(
    'error',
    (event) => {
      const el = event.target;
      if (!(el instanceof HTMLImageElement || el instanceof HTMLVideoElement)) return;
      const src = el.src;
      if (!src.startsWith(FILE_CDN_URL) || src.includes('s3=true')) return;
      event.stopPropagation();
      if (!_warnedS3Fallback) {
        _warnedS3Fallback = true;
        console.warn('[file-cdn] media failed to load from the CDN; retrying via the S3 copy');
      }
      el.src = `${src}${src.includes('?') ? '&' : '?'}s3=true`;
    },
    true,
  );
}

/** Clear cached key data, crypto key, and URL cache. Call on logout. */
export function clearFileCdnCache(): void {
  _keyData = null;
  _cryptoKey = null;
  _urlCache.clear();
}
